import React, { useEffect, useState } from "react";
import { 
  BarChart, Bar, XAxis, YAxis, 
  Tooltip as ChartTooltip, ResponsiveContainer, Cell, LabelList 
} from "recharts";
import { Spinner, Card, OverlayTrigger, Tooltip, Badge, Modal, Button } from "react-bootstrap";
import { FiInfo, FiX } from "react-icons/fi";

const BarreOrdiniOggi = () => {
  const [dataGrafico, setDataGrafico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((data) => {
        const ordini = data?.ORDINI_OGGI?.flat() || [];
        
        let sommaOF4G = 0;
        let sommaAltri = 0;

        ordini.forEach(item => {
          const valore = parseFloat(item.importo_doc) || 0;
          if (item.tipo_doc === "OF4G") {
            sommaOF4G += valore;
          } else {
            sommaAltri += valore;
          }
        });

        setDataGrafico([
          { name: "URGENTI", totale: sommaOF4G, color: "#b81f25" },
          { name: "STANDARD", totale: sommaAltri, color: "#adb5bd" }
        ]);
        
        setLoading(false);
      })
      .catch((err) => {
        console.error("Errore fetch dati:", err);
        setLoading(false);
      });
  }, []);

  const formatEuro = (value) => 
    new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR', 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2  
    }).format(value);

  // Tooltip esplicativo per l'header del widget
  const renderWidgetTooltip = (props) => (
    <Tooltip id="volume-info-tooltip" {...props}>
      <div style={{ textAlign: "left", fontSize: "0.75rem", padding: "4px" }}>
        Questo grafico mostra il fatturato totale (volume di vendita) degli ordini odierni, 
        confrontando l'importo dei documenti urgenti <strong>(OF4G)</strong> con quelli standard.
      </div>
    </Tooltip>
  );

  // Contenuto grafico riutilizzabile (Card vs Modale)
  const renderChartContent = (chartHeight = "100%") => (
    <>
      <div className="w-100" style={{ height: chartHeight, minHeight: chartHeight === "100%" ? "auto" : chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={dataGrafico}
            margin={{ top: 20, right: 80, left: 20, bottom: 20 }}
          >
            <XAxis type="number" hide />
            <YAxis 
              dataKey="name" 
              type="category" 
              stroke="#6c757d" 
              fontSize={11} 
              fontWeight={700}
              tickLine={false}
              axisLine={false}
            />
            <ChartTooltip 
              cursor={{ fill: '#f8f9fa' }}
              contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}
              formatter={(value) => [formatEuro(value), "Totale"]}
            />
            <Bar dataKey="totale" radius={[0, 10, 10, 0]} barSize={45}>
              {dataGrafico.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
              <LabelList 
                dataKey="totale" 
                position="right" 
                formatter={formatEuro} 
                style={{ fontSize: '0.8rem', fontWeight: 'bold', fill: '#333' }} 
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 p-3 rounded-3 bg-light d-flex justify-content-around">
        <div className="text-center">
          <div className="text-muted text-uppercase mb-1" style={{ fontSize: '0.6rem', fontWeight: '800' }}>Urgenti</div>
          <div className="fw-bold" style={{ color: "#b81f25", fontSize: '1.2rem' }}>
            {dataGrafico[0] ? formatEuro(dataGrafico[0].totale) : "€ 0,00"}
          </div>
        </div>
        <div style={{ width: '1px', background: '#dee2e6' }}></div>
        <div className="text-center">
          <div className="text-muted text-uppercase mb-1" style={{ fontSize: '0.6rem', fontWeight: '800' }}>Standard</div>
          <div className="fw-bold" style={{ color: "#444", fontSize: '1.2rem' }}>
            {dataGrafico[1] ? formatEuro(dataGrafico[1].totale) : "€ 0,00"}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <Card className="border-0 card shadow-lg overflow-hidden" style={{ borderRadius: '16px', height: '400px', background: '#fff' }}>
      
      {/* HEADER DELLA CARD PRINCIPALE */}
      <div
        className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white"
        style={{ zIndex: 110 }}
      >
        <div className="d-flex align-items-center" onClick={() => setShowFullscreenModal(true)} style={{ cursor: "pointer" }}>
          <span
            style={{
              fontWeight: "800",
              fontSize: "0.75rem",
              color: "#1a1a1a",
              letterSpacing: "1px",
              marginRight: "8px",
            }}
          >
            VOLUME VENDITA OGGI
          </span>
          <Badge bg="secondary" style={{ fontSize: "0.55rem", padding: "4px 6px" }}>ESPANDI</Badge>
          <OverlayTrigger
            placement="right"
            delay={{ show: 100, hide: 400 }}
            overlay={renderWidgetTooltip}
          >
            <span className="ms-2" onClick={(e) => e.stopPropagation()} style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
              <FiInfo size={14} color="#adb5bd" />
            </span>
          </OverlayTrigger>
        </div>
      </div>

      {/* CORPO DELLA CARD PRINCIPALE */}
      <Card.Body className="d-flex flex-column p-4 justify-content-center">
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" variant="danger" />
          </div>
        ) : (
          renderChartContent("100%")
        )}
      </Card.Body>

      {/* MODALE SCHERMO INTERO */}
      <Modal show={showFullscreenModal} onHide={() => setShowFullscreenModal(false)} fullscreen={true} animation={true}>
        <Modal.Body className="p-0 d-flex flex-column" style={{ background: "#f8f9fa" }}>
          <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center shadow-sm" style={{ zIndex: 110 }}>
            <div className="d-flex align-items-center">
              <span style={{ fontWeight: "800", fontSize: "0.9rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
                VOLUME VENDITA OGGI - VISTA ESPANSA
              </span>
              <OverlayTrigger placement="right" overlay={renderWidgetTooltip}>
                <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}><FiInfo size={16} color="#adb5bd" /></span>
              </OverlayTrigger>
            </div>
            <Button variant="outline-dark" className="d-flex align-items-center gap-1 border-0" onClick={() => setShowFullscreenModal(false)} style={{ borderRadius: "10px", height: "38px" }}>
              <FiX size={16} /> Chiudi
            </Button>
          </div>
          
          <div className="flex-grow-1 p-4 d-flex align-items-center justify-content-center" style={{ overflowY: "auto" }}>
            <div className="bg-white p-5 shadow-sm rounded-4 border w-100" style={{ maxWidth: "1000px" }}>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="danger" />
                </div>
              ) : (
                // Nel modale diamo un'altezza fissa generosa per slanciare le barre orizzontali
                renderChartContent("300px")
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>

    </Card>
  );
};  

export default BarreOrdiniOggi;